import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Smartphone, Download, Share, Plus, CheckCircle2, Info,
} from "lucide-react";
import usePWAInstall from "../hooks/usePWAInstall";

/**
 * InstallAppButton — discreet single-row button that appears inside
 * ProfileDialog only when the app isn't already installed.
 *
 * Behaviour:
 *   - Already installed (running in standalone mode) → renders nothing
 *   - Chrome captured `beforeinstallprompt` → 1-click native install
 *   - iOS Safari (no prompt event) → opens a small modal with the
 *     3 ordered "Compartilhar → Adicionar à Tela de Início" steps
 */
const InstallAppButton = () => {
  const { installed, canPrompt, platform, prompt } = usePWAInstall();
  const [iosOpen, setIosOpen] = useState(false);

  if (installed) return null;
  // Only show the button when there's a real install path. On
  // desktop browsers that don't satisfy PWA criteria, hide it.
  if (!canPrompt && platform !== "ios") return null;

  const handleClick = async () => {
    if (canPrompt) {
      const { outcome } = await prompt();
      if (outcome === "accepted") {
        toast.success("App instalado! Veja o ícone na sua tela inicial.");
      }
    } else if (platform === "ios") {
      setIosOpen(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-gradient-to-r from-emerald-50 to-white hover:from-emerald-100 hover:to-slate-50 transition group text-left"
        data-testid="install-app-btn"
      >
        <div className="w-9 h-9 rounded-md bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
          <Download className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">
            Instalar app no celular
          </p>
          <p className="text-[11px] text-slate-500">
            Acesso rápido sem abrir o navegador
          </p>
        </div>
        <Smartphone className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 transition" />
      </button>

      <IOSInstallDialog open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
};

const IOSInstallDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm" data-testid="install-ios-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-base">
          <Smartphone className="w-5 h-5 text-emerald-600" />
          Instalar no iPhone
        </DialogTitle>
        <DialogDescription className="text-xs">
          Como o iPhone não instala apps automaticamente, basta seguir
          estes 3 passos no Safari:
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 py-2">
        <Step
          num={1}
          icon={<Share className="w-5 h-5 text-blue-600" />}
          title="Toque no ícone Compartilhar"
          subtitle="A caixa com a seta para cima ↥ — fica na barra inferior do Safari."
        />
        <Step
          num={2}
          icon={<Plus className="w-5 h-5 text-slate-800" />}
          title="Role e toque em 'Adicionar à Tela de Início'"
          subtitle="Pode estar mais embaixo, na lista de opções."
        />
        <Step
          num={3}
          icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}
          title="Confirme 'Adicionar'"
          subtitle="O ícone da AVI aparece na sua tela inicial igual a qualquer outro app."
        />
      </div>

      <div className="flex items-start gap-2 p-2.5 bg-blue-50 rounded text-[11px] text-blue-800">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Importante: tem que estar no <strong>Safari</strong> (não Chrome ou outro).
        </span>
      </div>

      <DialogFooter>
        <Button
          onClick={() => onOpenChange(false)}
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          data-testid="install-ios-close"
        >
          Entendi
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const Step = ({ num, icon, title, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, x: -6 }}
    animate={{ opacity: 1, x: 0 }}
    className="flex gap-3 items-start"
  >
    <div className="flex flex-col items-center">
      <div className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-semibold flex items-center justify-center">
        {num}
      </div>
    </div>
    <div className="flex-1 min-w-0 pt-0.5">
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-sm font-medium text-slate-900">{title}</p>
      </div>
      <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
    </div>
  </motion.div>
);

export default InstallAppButton;
